import { useContext, useEffect, useRef, useState } from 'react';
import QRCodeStyling from 'qr-code-styling';
import './DownloadAppBanner.css';
import { UserContext } from '../contexts/user.context';
import { UserProviderValue } from '../models';
import { buildOneLink } from '../utils';

export function DownloadAppBanner() {
   const { user } = useContext<UserProviderValue>(UserContext);
   const [bannerVisible, setBannerVisible] = useState<boolean>(true);
   const qrContainerRef = useRef<HTMLDivElement>(null);
   const qrInstanceRef = useRef<QRCodeStyling | null>(null);

   // OneLink auto-routes to the correct store per OS and carries attribution +
   // user id into the install — never link the raw store URLs.
   const oneLink = buildOneLink(user?.id, 'badge');

   useEffect(() => {
      if (!bannerVisible || !qrContainerRef.current) return;
      const data = buildOneLink(user?.id, 'qr');

      if (qrInstanceRef.current) {
         qrInstanceRef.current.update({ data });
         return;
      }

      qrInstanceRef.current = new QRCodeStyling({
         // rendered at 2x and scaled down via CSS so the canvas stays crisp on retina
         width: 216,
         height: 216,
         // minimal quiet zone (Bay 2026-07-23: less white border) — level-H
         // correction keeps it scannable
         margin: 2,
         data,
         // level H tolerates ~30% damage, so the center logo doesn't break scanning
         qrOptions: { errorCorrectionLevel: 'H' },
         image: '/assets/images/download-app/qr-logo.png',
         imageOptions: { imageSize: 0.35, margin: 2, hideBackgroundDots: true },
         dotsOptions: { type: 'dots', color: '#000000' },
         cornersSquareOptions: { type: 'extra-rounded', color: '#68c072' },
         cornersDotOptions: { type: 'extra-rounded', color: '#68c072' },
         backgroundOptions: { color: '#ffffff' },
      });
      qrInstanceRef.current.append(qrContainerRef.current);
   }, [user?.id, bannerVisible]);

   useEffect(() => {
      const visible = localStorage.getItem('downloadAppBannerVisible');
      if (visible === 'false') {
         setBannerVisible(false);
      }
   }, []);

   return bannerVisible ? (
      <div className="banner download-app">
         <h2 className="download-app-title">
            Take GolfN with you.
            <br />
            <span className="green">More golf. More rewards</span>.
         </h2>

         <div className="download-app-qr">
            <div
               ref={qrContainerRef}
               role="img"
               aria-label="QR code to download the GolfN app"
            />
         </div>

         <div className="download-app-info">
            <div className="flex gap-2">
               <a
                  className="download-app-store-button"
                  href={oneLink}
                  target="_blank"
                  rel="noreferrer">
                  <img
                     src="/assets/images/download-app/apple.svg"
                     alt="Download on the App Store"
                  />
               </a>
               <a
                  className="download-app-store-button"
                  href={oneLink}
                  target="_blank"
                  rel="noreferrer">
                  <img
                     src="/assets/images/download-app/google-play.svg"
                     alt="Get it on Google Play"
                  />
               </a>
            </div>
            <span className="download-app-description">Scan to download. Same login, everywhere — rounds, rewards, and sweepstakes on the go.</span>
         </div>

         <img
            src="/assets/images/download-app/chevron.svg"
            className="download-app-chevron"
            alt=""
         />
         <img
            src="/assets/images/download-app/phone.png"
            className="download-app-phone"
            alt=""
         />

         <div
            className="cross"
            onClick={() => {
               setBannerVisible(false);
               localStorage.setItem('downloadAppBannerVisible', 'false');
            }}
         />
      </div>
   ) : null;
}
